import { createAsyncThunk } from "@reduxjs/toolkit";
import { Axios } from "api/axios";
import { api } from "helpers/api_url";

interface IssueToLabourFilters {
  search?: string;
  warehouse_id?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
  order?: "asc" | "desc";
  [key: string]: any;
}

/* -------------------------------------------
   DUMMY API PATHS (UPDATE LATER)
-------------------------------------------- */
const issueToLabourAPI = {
  inventory_item_by_id: "/issue-to-labour/inventory-item/",
  create: "/issue-to-labour/create",
  update: "/issue-to-labour/update/",
  listing: "/issue-to-labour/list",
  getByID: "/issue-to-labour/get",
  export_csv: "/issue-to-labour/export",
};

/* -------------------------------------------
   LIST ISSUE TO LABOUR
-------------------------------------------- */

export const fetchIssueToLabour = createAsyncThunk(
  "IssueToLabour/fetchIssueToLabour",
  async ({
    page,
    pageSize,
    search,
    sort,
    order,
    filters,
    warehouse_id,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any;
    warehouse_id: string;
  }) => {
    const params = new URLSearchParams();

    params.append("page", page.toString());
    params.append("page_size", pageSize.toString());
    if (search) params.append("search", search);

    if (sort) {
      const orderingValue = order === "desc" ? `-${sort}` : sort;
      params.append("ordering", orderingValue);
    }

    const formatDate = (date: any) => {
      if (!date) return "";
      const d = new Date(date);
      return d.toISOString().split("T")[0];
    };

    // handle filters
    if (filters?.length) {
      filters.forEach(
        ({
          filterField,
          filterValue,
        }: {
          filterField: string;
          filterValue: any;
        }) => {
          if (!filterField || filterValue == null) return;

          if (typeof filterValue === "object") {
            const { from_date, to_date } = filterValue;

            if (
              from_date &&
              ["created_at", "updated_at", "dispatch_date"].includes(
                filterField
              )
            ) {
              params.append(`${filterField}_from`, formatDate(from_date));
            }
            if (
              to_date &&
              ["created_at", "updated_at", "dispatch_date"].includes(
                filterField
              )
            ) {
              params.append(`${filterField}_to`, formatDate(to_date));
            }

            return;
          }

          params.append(filterField, String(filterValue));
        }
      );
    }

    const response: any = await Axios.get(
      `${
        api.warehouse.dispatch.issue_to_labour.listing
      }?warehouse_id=${warehouse_id}&${params.toString()}`
    );

    // console.log("Thunk response",response)
    return {
      data: response?.data?.data || [],
      rowCount: response?.data?.count || 0,
    };
  }
);

/* -------------------------------------------
   GET BY ID
-------------------------------------------- */

export const fetchIssueToLabourById = createAsyncThunk(
  "IssueToLabour/fetchIssueToLabourById",
  async (dispatch_id: string, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${api.warehouse.dispatch.issue_to_labour.getByID}/${dispatch_id}`
      );

      console.log("Thunk response", response);
      return response?.data?.data || {};
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ||
          "Failed to fetch issue to labour details"
      );
    }
  }
);

/* -------------------------------------------
   CREATE ISSUE TO LABOUR
-------------------------------------------- */

export const createIssueToLabour = createAsyncThunk(
  "IssueToLabour/createIssueToLabour",
  async (formData: FormData, { rejectWithValue }) => {
    try {
      const response: any = await Axios.post(
        api.warehouse.dispatch.issue_to_labour.create,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response?.data?.success) {
        return response.data.data;
      }

      const errorMsg =
        response?.data?.message ||
        response?.data?.error ||
        "Failed to create issue to labour";

      return rejectWithValue(errorMsg);
    } catch (error: any) {
      const res = error?.response?.data || {};
      return rejectWithValue({
        message:
          res?.message ||
          res?.error ||
          error?.message ||
          "Failed to create issue to labour",
        errors: res?.data?.errors || res?.errors,
        responseData: res,
      });
    }
  }
);

/* -------------------------------------------
   UPDATE ISSUE TO LABOUR
-------------------------------------------- */

export const updateIssueToLabour = createAsyncThunk(
  "IssueToLabour/updateIssueToLabour",
  async (
    { id, formData }: { id: string; formData: FormData },
    { rejectWithValue }
  ) => {
    try {
      const response: any = await Axios.put(
        `${api.warehouse.dispatch.issue_to_labour.update}${id}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response?.data?.success) {
        return response.data.data;
      }

      const errorMsg =
        response?.data?.message ||
        response?.data?.error ||
        "Failed to update issue to labour";

      return rejectWithValue(errorMsg);
    } catch (error: any) {
      const res = error?.response?.data || {};
      return rejectWithValue({
        message:
          res?.message ||
          res?.error ||
          error?.message ||
          "Failed to update issue to labour",
        errors: res?.data?.errors || res?.errors,
        responseData: res,
      });
    }
  }
);

/* -------------------------------------------
   GET INVENTORY ITEM BY INVENTORY_ITEM_ID
-------------------------------------------- */
export const getInventoryItemByInventoryItemIdIssueToLabour = createAsyncThunk(
  "IssueToLabour/getInventoryItemByInventoryItemIdIssueToLabour",
  async (
    {
      inventory_item_id,
      warehouse_id,
    }: {
      inventory_item_id: string;
      warehouse_id?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams();
      if (warehouse_id) params.append("warehouse_id", warehouse_id);

      const url = `${
        issueToLabourAPI.inventory_item_by_id
      }${inventory_item_id}${params.toString() ? `?${params.toString()}` : ""}`;

      const response: any = await Axios.get(url);

      if (response?.data?.success && response?.data?.data) {
        return response.data.data;
      }

      return rejectWithValue(
        response?.data?.message ||
          response?.data?.error ||
          "Failed to fetch inventory item"
      );
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to fetch inventory item. Please try again.";

      return rejectWithValue(errorMessage);
    }
  }
);

/* -------------------------------------------
   CSV EXPORT
-------------------------------------------- */
export const exportIssueToLabourCSV = async (
  filters: IssueToLabourFilters = {}
) => {
  console.log("Current Filters",filters)
  const params = new URLSearchParams({ export: "csv", ...filters } as any);

  const response = await Axios.get(
    `${api.warehouse.dispatch.issue_to_labour.listing}?${params.toString()}`,
    { responseType: "blob" }
  );

  return {
    blob: response.data,
    filename:
      response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") || "issueToLabour.csv",
  };
};
